// ============================================================================
// Roof Designer · Toolbar — Werkzeug-Palette (bereinigt)
// ----------------------------------------------------------------------------
// Links: Werkzeuge (Maßstab, Dach zeichnen, Hindernis, Auswahl) mit klarem
// Aktiv-Zustand. Rechts: Hintergrund-Aktionen (Dachbild laden/entfernen,
// Satellit ein/aus) und kompakte kWp-Live-Vorschau. Status-Sprache (ENTWURF,
// Nicht freigegeben, …) lebt jetzt zentral im Header + Ergebnis-Panel —
// nicht mehr als Chip-Wildwuchs hier. Kein Berechnungs-Code, nur Dispatch.
// ============================================================================
import { useState } from "react";
import type { DesignerState } from "../types.js";
import type { DesignerResult } from "../roof-engine.js";
import type { DesignerAction } from "../state.js";
import { fileToScaledDataUrl } from "../background.js";

interface Props {
  state: DesignerState;
  result: DesignerResult;
  dispatch: (a: DesignerAction) => void;
}

// ─── ManualGeoRefInput ────────────────────────────────────────────────────────
// Fallback-Eingabe: Satelliten-Hintergrund ohne importierte Dachfläche positionieren.
// Zeigt einen kompakten Koordinaten-Eingabe-Bereich (Lat/Lng), der nach Bestätigung
// SET_MANUAL_GEOREF dispatcht und damit backgroundGeoRef im State setzt.

interface ManualGeoRefProps {
  dispatch: (a: DesignerAction) => void;
}

function ManualGeoRefInput({ dispatch }: ManualGeoRefProps) {
  const [lat, setLat] = useState("47.0707");
  const [lng, setLng] = useState("15.4395");
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        className="rd-toolbar__btn"
        onClick={() => setOpen(true)}
        title="Satelliten-Hintergrund manuell positionieren (Koordinaten eingeben)"
      >
        <span className="rd-toolbar__icon">📍</span>
        <span className="rd-toolbar__label">Koordinaten</span>
      </button>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(15,23,42,0.8)", padding: "4px 8px", borderRadius: 6 }}>
      <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>Lat:</span>
      <input
        type="number" step="0.0001" value={lat}
        onChange={e => setLat(e.target.value)}
        style={{ width: 90, fontSize: 11, padding: "2px 4px", borderRadius: 4, border: "1px solid #334155", background: "#1e293b", color: "#e2e8f0" }}
      />
      <span style={{ fontSize: 11, color: "#94a3b8" }}>Lng:</span>
      <input
        type="number" step="0.0001" value={lng}
        onChange={e => setLng(e.target.value)}
        style={{ width: 90, fontSize: 11, padding: "2px 4px", borderRadius: 4, border: "1px solid #334155", background: "#1e293b", color: "#e2e8f0" }}
      />
      <button
        onClick={() => {
          const la = parseFloat(lat), lo = parseFloat(lng);
          if (isFinite(la) && isFinite(lo)) {
            dispatch({ type: "SET_MANUAL_GEOREF", lat: la, lng: lo });
            setOpen(false);
          }
        }}
        style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "#2563eb", color: "#fff", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}
      >
        Satellit laden
      </button>
      <button onClick={() => setOpen(false)} style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, background: "transparent", color: "#94a3b8", border: "none", cursor: "pointer" }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const TOOLS: { key: DesignerState["activeTool"]; label: string; icon: string; title: string }[] = [
  { key: "polygon",   label: "Dach zeichnen", icon: "⬡", title: "Klicken = Punkt, Doppelklick oder Startpunkt = Schliessen" },
  { key: "calibrate", label: "Maßstab", icon: "⇔", title: "Zwei Punkte klicken, Länge eingeben, Setzen" },
  { key: "obstacle",  label: "Hindernis", icon: "▪", title: "Klicken + Ziehen = Hindernis-Rechteck" },
  { key: "select",    label: "Auswahl", icon: "↖", title: "Klicken auf Hindernis zum Entfernen" },
];

export function Toolbar({ state, result, dispatch }: Props) {
  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      // DataURL statt Object-URL → übersteht Reload und wird mitgespeichert.
      const dataUrl = await fileToScaledDataUrl(file);
      dispatch({ type: "SET_BACKGROUND_IMAGE", url: dataUrl });
    } catch {
      // Lesefehler → kein State-Wechsel
    }
  };

  const kwpLabel =
    result.status === "ok" && result.moduleCount > 0
      ? `${result.moduleCount} Module · ${result.kwp?.toFixed(2) ?? "—"} kWp`
      : null;

  const hasGeoRef = state.backgroundGeoRef != null;
  const satelliteOn = hasGeoRef && state.backgroundGeoRef!.enabled;

  return (
    <div className="rd-toolbar">
      {/* Werkzeuge */}
      <div className="rd-toolbar__group" role="group" aria-label="Werkzeuge">
        {TOOLS.map((t) => (
          <button
            key={t.key}
            className={`rd-toolbar__btn${state.activeTool === t.key ? " rd-toolbar__btn--active" : ""}`}
            onClick={() => dispatch({ type: "SET_ACTIVE_TOOL", tool: t.key })}
            title={t.title}
          >
            <span className="rd-toolbar__icon">{t.icon}</span>
            <span className="rd-toolbar__label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="rd-toolbar__sep" />

      {/* Zeichnung */}
      <div className="rd-toolbar__group">
        <button
          className="rd-toolbar__btn"
          onClick={() => dispatch({ type: "RESET_POLYGON" })}
          title="Dach-Polygon neu zeichnen"
        >
          <span className="rd-toolbar__icon">↩</span>
          <span className="rd-toolbar__label">Polygon neu</span>
        </button>
      </div>

      <div className="rd-toolbar__sep" />

      {/* Hintergrund (reale Planungsbasis) */}
      <div className="rd-toolbar__group" role="group" aria-label="Hintergrund">
        <label className="rd-toolbar__btn" title="Dachbild (Drohnenaufnahme, Grundriss) als Hintergrund laden — wird mitgespeichert">
          <span className="rd-toolbar__icon">📷</span>
          <span className="rd-toolbar__label">Dachbild laden</span>
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { void handleImage(e); }} />
        </label>

        {state.backgroundImage && (
          <button
            className="rd-toolbar__btn"
            onClick={() => dispatch({ type: "SET_BACKGROUND_IMAGE", url: null })}
            title="Hintergrundbild entfernen"
          >
            <span className="rd-toolbar__icon">✕</span>
            <span className="rd-toolbar__label">Bild entf.</span>
          </button>
        )}

        {hasGeoRef && (
          <button
            className={`rd-toolbar__btn${satelliteOn ? " rd-toolbar__btn--active" : ""}`}
            data-testid="satellite-toggle"
            onClick={() => dispatch({ type: "SET_SATELLITE_ENABLED", enabled: !satelliteOn })}
            title="Satelliten-Hintergrund (lagerichtig zur importierten Dachfläche) ein-/ausblenden"
          >
            <span className="rd-toolbar__icon">🛰</span>
            <span className="rd-toolbar__label">Satellit {satelliteOn ? "aus" : "ein"}</span>
          </button>
        )}
      </div>

      {/* ── Manuelle Koordinaten-Eingabe (Fallback ohne importierte Dachfläche) ── */}
      {!hasGeoRef && (
        <div className="rd-toolbar__group">
          <ManualGeoRefInput dispatch={dispatch} />
        </div>
      )}

      {/* Live-Vorschau (rechts) */}
      <div className="rd-toolbar__info">
        {kwpLabel && (
          <span className="rd-toolbar__chip rd-toolbar__chip--ok">{kwpLabel}</span>
        )}
      </div>
    </div>
  );
}
